import * as turf from '@turf/turf';
import * as GeoJSON from 'geojson';
import * as fp from 'lodash/fp';
import { Color, GetPickingInfoParams, PickingInfo } from '@deck.gl/core';
import { crdtClient } from '@larki/mp';
import { stateSchema, tempStateSchema } from '@larki/mp/lib/state';
import { DataType } from '@larki/mp/lib/dataType';
import { Corner, rectangleLib } from '@larki/mp/lib/rectangle';
import { objectAdd, objectAssign, objectMap } from '@larki/shared/object';

import { DeleteVertexLayerPickingDataT_ } from '../layers/deleteVertexLayer';
import { AddVertexLayerPickingDataT_ } from '../layers/addVertexLayer';

export * from '@larki/mp/lib/shape';

export type TentativeHandle =
  | GeoJSON.Feature<GeoJSON.Point, TentativePolygonHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativeRectangleHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativePathHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativeBooleanHandleProperties>;

const convertStateToJson = (
  state: crdtClient.LoroDoc,
  tempState: crdtClient.LoroDoc
) => {
  const stateJson = stateSchema.parse(state.toJSON());
  const tempStateJson = tempStateSchema.parse(tempState.toJSON());
  return {
    ...objectAssign(stateJson, 'shapes', (shapes) =>
      shapes?.map((shape) =>
        objectAdd(
          shape,
          'hidden',
          tempStateJson.hidden_map?.[shape.id] ?? false
        )
      )
    ),
    ...tempStateJson,
  };
};
export type JsonState = ReturnType<typeof convertStateToJson>;
export type ShapeDetails = Pick<
  Required<JsonState>['shapes'][number],
  'id' | 'type' | 'name' | 'customOrderId' | 'dataType' | 'hidden' | 'color'
> & { active: boolean };

const checkStateIsValid = (
  state: crdtClient.LoroDoc,
  tempState: crdtClient.LoroDoc
) =>
  stateSchema.safeParse(state.toJSON()).success &&
  tempStateSchema.safeParse(tempState.toJSON()).success;

type _BaseMapFeatureProperties = {
  featureType: 'shape' | 'handle';
};
export interface _BaseTentativeShapeHandleProperties
  extends _BaseMapFeatureProperties {
  featureType: 'handle';
  handleType: 'tentative';
  shapeId: string;
}
export interface TentativePolygonHandleProperties
  extends _BaseTentativeShapeHandleProperties {
  shapeType: 'polygon';
}
export interface TentativeRectangleHandleProperties
  extends _BaseTentativeShapeHandleProperties {
  shapeType: 'rectangle';
}
export interface TentativePathHandleProperties
  extends _BaseTentativeShapeHandleProperties {
  shapeType: 'path';
}
export interface TentativeBooleanHandleProperties
  extends _BaseTentativeShapeHandleProperties {
  shapeType: 'boolean';
  parentIndex: number;
}
export const createTentativePolygonHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    TentativePolygonHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<TentativePolygonHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'tentative',
    shapeType: 'polygon',
  });
export const createTentativeRectangleHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    TentativeRectangleHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<TentativeRectangleHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'tentative',
    shapeType: 'rectangle',
  });
export const createTentativePathHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    TentativePathHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<TentativePathHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'tentative',
    shapeType: 'path',
  });
export const createTentativeBooleanHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    TentativeBooleanHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<TentativeBooleanHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'tentative',
    shapeType: 'boolean',
  });

export interface _BaseShapeHandleProperties extends _BaseMapFeatureProperties {
  featureType: 'handle';
  handleType: 'shape';
  shapeIndex: number;
  hidden: boolean;
  dataType: DataType;
}
export interface PolygonHandleProperties extends _BaseShapeHandleProperties {
  shapeType: 'polygon';
  vertexIndex: number;
}
export interface RectangleHandleProperties extends _BaseShapeHandleProperties {
  shapeType: 'rectangle';
  corner: Corner;
}
export interface PathHandleProperties extends _BaseShapeHandleProperties {
  shapeType: 'path';
  vertexIndex: number;
}
export interface BooleanHandleProperties extends _BaseShapeHandleProperties {
  shapeType: 'boolean';
  holeIndex: number;
  vertexIndex: number;
}
export const createPolygonHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    PolygonHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<PolygonHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'shape',
    shapeType: 'polygon',
  });
export const createRectangleHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    RectangleHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<RectangleHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'shape',
    shapeType: 'rectangle',
  });
export const createPathHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    PathHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<PathHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'shape',
    shapeType: 'path',
  });
export const createBooleanHandle = (
  coordinate: GeoJSON.Position,
  properties: Omit<
    BooleanHandleProperties,
    'featureType' | 'handleType' | 'shapeType'
  >
) =>
  turf.point<BooleanHandleProperties>(coordinate, {
    ...properties,
    featureType: 'handle',
    handleType: 'shape',
    shapeType: 'boolean',
  });

export interface PathLinesProperties {
  source: GeoJSON.Position;
  target: GeoJSON.Position;
  lineColor: Color;
}

export interface MapShapeFeatureProperties extends _BaseMapFeatureProperties {
  featureType: 'shape';
  shapeIndex: number;
  hidden: boolean;
  dataType: DataType;
  active?: boolean;
  pathLines?: PathLinesProperties;
  color?: [number, number, number];
}
export type MapShapeFeature = GeoJSON.Feature<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  MapShapeFeatureProperties
>;

export function interpolateState(
  state: JsonState,
  otherCursors: Map<string, GeoJSON.Position>,
  myCursor: GeoJSON.Position | null,
  myClientId: string
): JsonState {
  function getCursor(clientId: string) {
    return clientId === myClientId ? myCursor : otherCursors.get(clientId);
  }

  const shapeIndexToDragClients = new Map<
    number,
    (Required<JsonState>['clients'][string] & { id: string })[]
  >();
  for (const [clientId, client] of fp.entries(state.clients ?? {})) {
    if (client.drag_shape_index !== undefined) {
      if (!shapeIndexToDragClients.has(client.drag_shape_index)) {
        shapeIndexToDragClients.set(client.drag_shape_index, []);
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      shapeIndexToDragClients
        .get(client.drag_shape_index)!
        .push({ ...client, id: clientId });
    }
  }

  return {
    tentative_shapes: objectMap((shape, clientId) => {
      const cursor = getCursor(clientId);
      if (!cursor) return shape;
      if (shape.type === 'polygon') {
        const { type, id, exterior } = shape;
        return {
          type,
          id,
          exterior: [...exterior, cursor],
        };
      } else if (shape.type === 'rectangle') {
        const { type, id, initial } = shape;
        return {
          type,
          id,
          initial,
          final: cursor,
        };
      } else if (shape.type === 'path') {
        const { type, id, vertices } = shape;
        return {
          type,
          id,
          vertices: [...vertices, cursor],
        };
      } else if (shape.type === 'boolean') {
        const { type, id, parent_index, vertices } = shape;
        return {
          type,
          id,
          parent_index,
          vertices: [...vertices, cursor],
        };
      }
      return shape;
    }, state.tentative_shapes ?? {}),
    shapes: state.shapes?.map((shape, shapeIndex) => {
      const dragClients = shapeIndexToDragClients.get(shapeIndex);
      if (!dragClients) return shape;

      if (shape.type === 'polygon') {
        const { exterior, holes, ...rest } = shape;

        for (const client of dragClients) {
          const cursor = getCursor(client.id);
          if (!cursor) continue;
          if (client.drag_hole_index !== undefined) {
            if (holes && client.drag_vertex_index !== undefined) {
              holes[client.drag_hole_index][client.drag_vertex_index] = cursor;
            }
          } else if (client.drag_vertex_index !== undefined) {
            exterior[client.drag_vertex_index] = cursor;
          }
        }

        return {
          exterior,
          holes,
          ...rest,
        };
      } else if (shape.type === 'rectangle') {
        const { exterior, holes, ...rest } = shape;

        for (const client of dragClients) {
          const cursor = getCursor(client.id);
          if (!cursor) continue;

          if (client.drag_hole_index !== undefined) {
            if (holes && client.drag_vertex_index !== undefined) {
              holes[client.drag_hole_index][client.drag_vertex_index] = cursor;
            }
          } else if (client.drag_corner) {
            const [cornerH, cornerV] = rectangleLib.getAdjacentCorners(
              client.drag_corner
            );
            exterior[client.drag_corner] = cursor;
            exterior[cornerH] = [exterior[cornerH][0], cursor[1]];
            exterior[cornerV] = [cursor[0], exterior[cornerV][1]];
          }
        }

        return {
          exterior,
          holes,
          ...rest,
        };
      } else if (shape.type === 'path') {
        const { vertices, ...rest } = shape;

        for (const client of dragClients) {
          const cursor = getCursor(client.id);
          if (!cursor) continue;
          if (client.drag_vertex_index !== undefined) {
            vertices[client.drag_vertex_index] = cursor;
          }
        }

        return {
          vertices,
          ...rest,
        };
      }

      return shape;
    }),
    clients: state.clients,
  };
}

export type PolygonHandleFeature = GeoJSON.Feature<
  GeoJSON.Point,
  PolygonHandleProperties
>;
export type RectangleHandleFeature = GeoJSON.Feature<
  GeoJSON.Point,
  RectangleHandleProperties
>;
export type PathHandleFeature = GeoJSON.Feature<
  GeoJSON.Point,
  PathHandleProperties
>;
export type BooleanHandleFeature = GeoJSON.Feature<
  GeoJSON.Point,
  BooleanHandleProperties
>;
export type DeletableHandleFeature =
  | PolygonHandleFeature
  | PathHandleFeature
  | BooleanHandleFeature;

export type MidpointProperties = {
  shapeIndex: number;
  vertexIndexBefore: number;
} & (
  | {
      shapeType: 'polygon' | 'path';
    }
  | {
      shapeType: 'boolean';
      holeIndex: number;
    }
);
export type MidpointFeature = GeoJSON.Feature<
  GeoJSON.Point,
  MidpointProperties
>;

export type PickingDataT_ =
  | GeoJSON.Feature<GeoJSON.Point, TentativePolygonHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativeRectangleHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativePathHandleProperties>
  | GeoJSON.Feature<GeoJSON.Point, TentativeBooleanHandleProperties>
  | PolygonHandleFeature
  | RectangleHandleFeature
  | PathHandleFeature
  | BooleanHandleFeature
  | MapShapeFeature;
export type PickingExtraInfo_ = {
  handle?:
    | TentativePolygonHandleProperties
    | TentativeRectangleHandleProperties
    | TentativePathHandleProperties
    | TentativeBooleanHandleProperties
    | PolygonHandleProperties
    | RectangleHandleProperties
    | PathHandleProperties
    | BooleanHandleProperties;
  mapShapeFeature?: MapShapeFeatureProperties;
  popup?: DeleteVertexLayerPickingDataT_ | AddVertexLayerPickingDataT_;
};
export type GetPickingInfoParams_ = GetPickingInfoParams<
  PickingDataT_,
  PickingExtraInfo_
>;
export type PickingInfo_ = PickingInfo<PickingDataT_, PickingExtraInfo_>;

export function createPolygonMidpointFeature(
  coordinate: GeoJSON.Position,
  shapeIndex: number,
  vertexIndexBefore: number
): MidpointFeature {
  return turf.point(coordinate, {
    shapeType: 'polygon',
    shapeIndex,
    vertexIndexBefore,
  });
}

export function createPathMidpointFeature(
  coordinate: GeoJSON.Position,
  shapeIndex: number,
  vertexIndexBefore: number
): MidpointFeature {
  return turf.point(coordinate, {
    shapeType: 'path',
    shapeIndex,
    vertexIndexBefore,
  });
}

export function createBooleanMidpointFeature(
  coordinate: GeoJSON.Position,
  shapeIndex: number,
  holeIndex: number,
  vertexIndexBefore: number
): MidpointFeature {
  return turf.point(coordinate, {
    shapeType: 'boolean',
    shapeIndex,
    holeIndex,
    vertexIndexBefore,
  });
}
